import { Link } from 'react-router-dom'
import type {
  DuplicateCandidate,
  DuplicateReasonCode,
  DuplicateSubscriberSummary,
} from '../api/client'
import { giftRole, type GiftRole } from '../lib/giftRole'

// Matching evidence is expressed three ways on the card:
//   - A row of pills at the top ("email · last_name · zip5")
//   - Per-field highlighting inside the A/B panels
//   - Tier badge on the left edge
// Keep pills and highlighting consistent — one is a scan-level summary, the
// other is the drill-down.

const TIER_COLOR: Record<string, string> = {
  auto_merge: 'text-amber-glow border-amber-glow',
  review_high: 'text-paper border-paper-dim',
  review_med: 'text-paper-dim border-ink-500',
  ignore: 'text-paper-trace border-ink-500',
}

const TIER_LABEL: Record<string, string> = {
  auto_merge: 'auto-merge ready',
  review_high: 'high confidence',
  review_med: 'needs review',
  ignore: 'low',
}

const GIFT_ROLE_LABEL: Record<GiftRole, string> = {
  giver: 'gift giver',
  receiver: 'gift receiver',
  both: 'gave + received',
}

const GIFT_ROLE_COLOR: Record<GiftRole, string> = {
  giver: 'text-radar-green border-radar-green/50 bg-radar-green/10',
  receiver: 'text-radar-cyan border-radar-cyan/50 bg-radar-cyan/10',
  both: 'text-radar-violet border-radar-violet/50 bg-radar-violet/10',
}

const GIFT_ROLE_HINT: Record<GiftRole, string> = {
  giver: 'Paid for a gift subscription on the other account',
  receiver: 'Got its subscription as a gift from the other account',
  both: 'The two accounts gave gift subscriptions to each other',
}

export function GiftRoleTag({ role }: { role: GiftRole | null }) {
  if (!role) return null
  return (
    <span
      title={GIFT_ROLE_HINT[role]}
      className={`font-mono text-[10px] uppercase tracking-label px-1.5 py-0.5 border rounded-sm ${GIFT_ROLE_COLOR[role]}`}
    >
      {GIFT_ROLE_LABEL[role]}
    </span>
  )
}

type Props = {
  candidate: DuplicateCandidate
  onReview: (id: number) => void
  onMerge?: (id: number) => void
  compact?: boolean
}

export function DuplicatePairCard({ candidate, onReview, onMerge, compact = false }: Props) {
  const { a, b, reason_codes, tier, score, gift_links } = candidate
  const matchedFields = new Set(
    reason_codes.map((rc) => matchedField(rc)).filter((x): x is string => x !== null),
  )

  return (
    <div className="border border-ink-500 bg-ink-900/40 rounded-sm mb-2 overflow-hidden">
      {/* Top meta row: tier badge · score · reason pills · action */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-ink-500/70 bg-ink-900/60">
        <span
          className={`font-mono text-[10px] uppercase tracking-label px-2 py-0.5 border rounded-sm ${TIER_COLOR[tier] ?? ''}`}
        >
          {TIER_LABEL[tier] ?? tier}
        </span>
        <span className="font-mono text-[11px] text-paper-dim tabular-nums">
          score {score.toFixed(2)}
        </span>
        <div className="flex flex-wrap gap-1">
          {reason_codes.map((rc, i) => (
            <ReasonPill key={i} rc={rc} />
          ))}
        </div>
        {gift_links?.length > 0 && (
          <span
            title="One account bought a gift subscription for the other: usually two people sharing an email. Consider Keep both, related."
            className="font-mono text-[10px] uppercase tracking-label px-1.5 py-0.5 border rounded-sm text-radar-green border-radar-green/40"
          >
            gift pair
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {onMerge && tier === 'auto_merge' && (
            <button
              onClick={() => onMerge(candidate.id)}
              className="font-mono text-[11px] text-amber-glow hover:underline"
            >
              merge →
            </button>
          )}
          <button
            onClick={() => onReview(candidate.id)}
            className="font-mono text-[11px] text-paper-dim hover:text-paper"
          >
            open review →
          </button>
        </div>
      </div>

      {/* Side-by-side subscriber diff */}
      <div className="grid grid-cols-2 divide-x divide-ink-500/70">
        <SubscriberPanel
          sub={a} matchedFields={matchedFields} side="A" compact={compact}
          giftRole={giftRole(gift_links, a?.id)}
          olderSide={olderSide(a?.member_since ?? null, b?.member_since ?? null) === 'a'}
        />
        <SubscriberPanel
          sub={b} matchedFields={matchedFields} side="B" compact={compact}
          giftRole={giftRole(gift_links, b?.id)}
          olderSide={olderSide(a?.member_since ?? null, b?.member_since ?? null) === 'b'}
        />
      </div>
    </div>
  )
}

function olderSide(a: string | null, b: string | null): 'a' | 'b' | null {
  if (!a && !b) return null
  if (!a) return 'b'
  if (!b) return 'a'
  return a < b ? 'a' : a > b ? 'b' : null
}

function SubscriberPanel({
  sub, matchedFields, side, compact, olderSide, giftRole: role,
}: {
  sub: DuplicateSubscriberSummary | null
  matchedFields: Set<string>
  side: string
  compact: boolean
  olderSide: boolean
  giftRole: GiftRole | null
}) {
  if (!sub) {
    return (
      <div className="p-3 text-paper-trace italic text-[12px]">
        subscriber {side} missing
      </div>
    )
  }
  const name = [sub.first_name, sub.last_name].filter(Boolean).join(' ') || '—'
  const loc = [sub.city, sub.state].filter(Boolean).join(', ')
  return (
    <div className="p-3">
      <div className="flex items-center gap-2 mb-2">
        <div className="font-mono text-[10px] text-paper-trace uppercase tracking-label">{side}</div>
        <Link
          to={`/subscribers/${sub.id}`}
          className="font-mono text-[11px] text-paper-trace hover:text-paper underline"
        >
          acct {sub.simplecirc_account_id}
        </Link>
        <GiftRoleTag role={role} />
        {olderSide && (
          <span
            className="ml-auto font-mono text-[10px] uppercase tracking-label text-amber-glow"
            title="Older 'member since' date — survivorship rule tiebreaker"
          >
            older
          </span>
        )}
      </div>
      <div className="space-y-1 text-[13px]">
        <Field
          label="name"
          value={name}
          matched={matchedFields.has('first_name') || matchedFields.has('last_name')}
        />
        <Field
          label="email"
          value={sub.email ?? '—'}
          mono
          matched={matchedFields.has('email') || matchedFields.has('email_local')}
        />
        {!compact && (
          <Field
            label="zip"
            value={sub.zipcode ?? '—'}
            mono
            matched={matchedFields.has('zip5')}
          />
        )}
        {loc && (
          <Field label="city" value={loc} matched={false} />
        )}
        <Field
          label="since"
          value={formatMemberSince(sub.member_since)}
          mono
          matched={false}
          accent={olderSide}
        />
      </div>
    </div>
  )
}

function formatMemberSince(ts: string | null): string {
  if (!ts) return '—'
  // Input is an ISO datetime; show as YYYY-MM-DD in the list view.
  return ts.slice(0, 10)
}

function Field({
  label, value, mono, matched, accent,
}: {
  label: string
  value: string
  mono?: boolean
  matched: boolean
  // "accent" — non-match amber highlight (e.g., tiebreaker-relevant field).
  accent?: boolean
}) {
  return (
    <div className="flex gap-3 items-baseline">
      <div className="font-mono text-[10px] uppercase tracking-label text-paper-trace w-12 shrink-0">
        {label}
      </div>
      <div
        className={[
          mono ? 'font-mono text-[12px]' : 'text-[13px]',
          matched
            ? 'text-amber-glow bg-amber-glow/5 px-1 rounded-sm'
            : accent
            ? 'text-amber-glow'
            : 'text-paper',
        ].join(' ')}
      >
        {value}
      </div>
    </div>
  )
}

function ReasonPill({ rc }: { rc: DuplicateReasonCode }) {
  const isFuzzy = rc.kind === 'fuzzy'
  return (
    <span
      title={rc.detail}
      className={`font-mono text-[10px] px-1.5 py-0.5 rounded-sm border ${
        isFuzzy
          ? 'text-paper-dim border-ink-500 border-dashed'
          : 'text-amber-glow border-amber-glow/40 bg-amber-glow/5'
      }`}
    >
      {displayFeature(rc.feature)}
      {isFuzzy && '~'}
    </span>
  )
}

function matchedField(rc: DuplicateReasonCode): string | null {
  // Map reason-code feature → the field name we highlight in the panel.
  switch (rc.feature) {
    case 'email':
    case 'email_local':
    case 'last_name':
    case 'first_name':
    case 'phone':
    case 'zip5':
    case 'street':
      return rc.feature
    default:
      return null
  }
}

function displayFeature(feature: string): string {
  switch (feature) {
    case 'email_local': return 'email local'
    case 'last_name': return 'last'
    case 'first_name': return 'first'
    default: return feature
  }
}
